import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { clearLoggedIn } from "../lib/authState";
import { API_URL, getAuthConfig, labRoleLabel } from "../lab/shared";
import LabOverview from "../lab/LabOverview";
import OnboardingModule from "../lab/OnboardingModule";
import StaffModule from "../lab/StaffModule";
import PatientsModule from "../lab/PatientsModule";
import ReceptionModule from "../lab/ReceptionModule";
import PreleveurModule from "../lab/PreleveurModule";
import ActTypesModule from "../lab/ActTypesModule";
import AgendaModule from "../lab/AgendaModule";
import PhlebotomyModule from "../lab/PhlebotomyModule";
import LabStatsModule from "../lab/LabStatsModule";
import SILIntegrationModule from "../lab/SILIntegrationModule";
import SupportModule from "../lab/SupportModule";
import AuditModule from "../lab/AuditModule";
import VitrineModule from "../lab/VitrineModule";
import PlanningModule from "../lab/PlanningModule";

/*
 * Tableau de bord dédié d'un laboratoire (reconstruction complète, voir le
 * plan de reconstruction, Phase 7). Contrairement à HopitalDashboard.js
 * (partagé entre hôpital ET labo via des branches establishment_type), ce
 * fichier ne sert QUE les établissements labo, appelle uniquement
 * GET /api/lab/me (backend/routes/lab_staff.py) et n'a besoin d'aucun filtre
 * LAB_MODULES ni de table de traduction de rôles (les rôles labo ont déjà
 * leur vrai nom, voir frontend/src/lab/shared.js::LAB_ROLE_LABELS).
 *
 * Construit par copie/adaptation délibérée de la coquille de
 * HopitalDashboard.js plutôt que par extraction de composants partagés :
 * duplication de code acceptée en échange d'un risque de régression nul sur
 * le tableau de bord hôpital existant (aucune ligne de HopitalDashboard.js
 * n'est touchée par ce fichier).
 *
 * Périmètre livré jusqu'ici (PR6a à PR6j) : Vue d'ensemble, Mise en route,
 * Personnel, Patients, Accueil, Écran préleveur, Types d'actes, Agenda,
 * Prélèvements, Statistiques, Intégration SIL, Aide & Support, Traçabilité,
 * Ma vitrine, Planning — les modules dont la route backend dédiée existe
 * déjà (PR3/PR3b, PR6c pour Types d'actes/Agenda/Horaires, PR6d pour
 * Prélèvements, PR6e pour Statistiques — aucun changement backend pour ce
 * dernier, /api/lab/reception/stats/trend existait déjà depuis PR3b — PR6f
 * pour Intégration SIL, nouveau fork backend/routes/lab/sil_integration.py —
 * PR6g pour Aide & Support, nouveau fork backend/routes/lab/support.py pour
 * le côté établissement uniquement, le côté équipe Tabbibak dans
 * routes/support.py restant partagé — PR6h pour Traçabilité, nouveau fork en
 * lecture seule backend/routes/lab/audit.py, db.audit_logs étant déjà rempli
 * par chaque route labo existante via write_audit_log — PR6i pour Ma
 * vitrine, nouveau fork des mutations backend/routes/lab/vitrine.py,
 * GET /api/lab/me existant déjà depuis PR3 — PR6j pour Planning, seul module
 * NON dérivé d'un fork direct : le module hôpital original manipule des
 * notions absentes d'un labo autonome (service/département, salle ou lit,
 * rôle "medecin" avec service+salle obligatoires, couverture par service) ;
 * backend/routes/lab/planning.py réécrit la logique générique [repos
 * réglementaire entre deux créneaux, brouillon → publié, remplacement d'un
 * agent absent] sur de nouvelles collections dédiées db.lab_shifts/
 * db.lab_leaves, sans les notions hôpital ci-dessus). Tous les modules du
 * plan de reconstruction sont désormais couverts.
 */
const MODULES = [
  { id: "overview", label: "Vue d'ensemble" },
  { id: "onboarding", label: "Mise en route" },
  { id: "reception", label: "Accueil" },
  { id: "preleveur", label: "Écran préleveur" },
  { id: "agenda", label: "Agenda" },
  { id: "actes", label: "Types d'actes" },
  { id: "phlebotomy", label: "Prélèvements" },
  { id: "planning", label: "Planning" },
  { id: "stats", label: "Statistiques" },
  { id: "sil", label: "Intégration SIL" },
  { id: "staff", label: "Personnel" },
  { id: "patients", label: "Patients" },
  { id: "support", label: "Aide & Support" },
  { id: "audit", label: "Traçabilité" },
  { id: "vitrine", label: "Ma vitrine" },
];

const MODULE_PERMS = {
  overview: "reception:read",
  onboarding: "staff:read",
  reception: "reception:read",
  preleveur: "reception:read",
  agenda: "agenda:read",
  actes: "acts:read",
  phlebotomy: "phlebotomy:read",
  planning: "planning:read",
  stats: "stats:read",
  sil: "integration:read",
  staff: "staff:read",
  patients: "patients:read",
  support: "support:read",
  audit: "audit:read",
  vitrine: "staff:write",
};

function LaboDashboard() {
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [ctx, setCtx] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setShowGlobalSearch(true);
      } else if (e.key === "Escape") {
        setShowGlobalSearch(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/lab/me`, getAuthConfig())
      .then((r) => setCtx(r.data))
      .catch((e) => {
        const code = e.response?.status;
        if (code === 401) setPageError("Vous n'êtes pas connecté. Connectez-vous d'abord.");
        else if (code === 403) setPageError("Votre compte n'est rattaché à aucun laboratoire.");
        else setPageError("Impossible de charger votre établissement.");
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B", fontFamily: "system-ui, sans-serif" }}>
        Chargement de votre espace...
      </div>
    );

  if (pageError)
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, fontFamily: "system-ui, sans-serif", padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: "#0F172A" }}>Espace laboratoire</div>
        <div style={{ fontSize: 14, color: "#A32D2D", maxWidth: 420 }}>{pageError}</div>
        <a href="/login" style={{ padding: "10px 20px", background: "#185FA5", color: "#fff", borderRadius: 8, textDecoration: "none", fontSize: 14 }}>Se connecter</a>
      </div>
    );

  const est = ctx?.establishment || {};
  const can = (p) => (ctx?.permissions || []).includes(p);
  const moduleVisible = (m) => {
    const perm = MODULE_PERMS[m.id];
    return perm ? can(perm) : false;
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "system-ui, sans-serif", background: "#F8FAFC" }}>
      {/* Sidebar */}
      <aside style={{ width: 240, background: "#fff", borderRight: "1px solid #E2E8F0", padding: "20px 0", flexShrink: 0 }}>
        <div style={{ padding: "0 20px 16px", borderBottom: "1px solid #F1F5F9" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>{est.name || "Laboratoire"}</div>
          <div style={{ fontSize: 11, color: "#185FA5", marginTop: 4, background: "#E6F1FB", display: "inline-block", padding: "2px 8px", borderRadius: 12 }}>
            Laboratoire · {labRoleLabel(ctx.establishment_role)}
          </div>
        </div>
        <nav style={{ marginTop: 12 }}>
          {MODULES.filter(moduleVisible).map((m) => (
            <button
              key={m.id}
              onClick={() => setActiveTab(m.id)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
                padding: "10px 20px", border: "none", background: activeTab === m.id ? "#E6F1FB" : "transparent",
                color: activeTab === m.id ? "#185FA5" : "#334155",
                fontSize: 14, cursor: "pointer", textAlign: "left",
                fontWeight: activeTab === m.id ? 600 : 400,
              }}
            >
              {m.label}
            </button>
          ))}
        </nav>
        <div style={{ padding: "16px 20px", marginTop: 12, borderTop: "1px solid #F1F5F9", display: "flex", flexDirection: "column", gap: 10 }}>
          <a href="/settings" style={{ fontSize: 13, color: "#185FA5", textDecoration: "none" }}>🔒 Changer mon mot de passe</a>
          <a href="/" style={{ fontSize: 13, color: "#64748B", textDecoration: "none" }}>← Retour à l'accueil</a>
          <button
            onClick={async () => {
              try { await axios.post(`${API_URL}/api/auth/logout`, {}, getAuthConfig()); } catch (e) { /* on déconnecte quand même */ }
              clearLoggedIn();
              window.location.href = "/login";
            }}
            style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", padding: 0, textAlign: "left", cursor: "pointer" }}
          >⎋ Se déconnecter</button>
        </div>
      </aside>

      {/* Contenu */}
      <main style={{ flex: 1, padding: "28px 32px", overflow: "auto" }}>
        {/* Correctif L-CTRLK-MORT : le raccourci Ctrl+K (ecouteur clavier
            ci-dessus) mettait deja a jour showGlobalSearch, mais aucun
            composant ne le lisait jamais — recopie depuis HopitalDashboard.js
            sans jamais porter le bouton ni la modale qui vont avec. */}
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <button
            onClick={() => setShowGlobalSearch(true)}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, color: "#64748B", fontSize: 13, cursor: "pointer" }}
          >
            🔍 Rechercher un patient
            <span style={{ fontSize: 11, color: "#94A3B8", border: "1px solid #E2E8F0", borderRadius: 4, padding: "1px 5px" }}>Ctrl+K</span>
          </button>
        </div>
        {showGlobalSearch && can("patients:read") && (
          <LabGlobalSearchModal
            onClose={() => setShowGlobalSearch(false)}
            onSelectPatient={(patient) => {
              setActiveTab("patients");
              window.dispatchEvent(new CustomEvent("tabbibak:searchPatient", { detail: { query: patient.patient_number || `${patient.first_name} ${patient.last_name}` } }));
              setShowGlobalSearch(false);
            }}
          />
        )}
        {activeTab === "overview" && <LabOverview ctx={ctx} est={est} />}
        {activeTab === "onboarding" && can("staff:read") && <OnboardingModule can={can} clinicId={ctx.clinic_id} setActiveTab={setActiveTab} />}
        {activeTab === "reception" && can("reception:read") && <ReceptionModule can={can} />}
        {activeTab === "preleveur" && can("reception:read") && <PreleveurModule can={can} />}
        {activeTab === "agenda" && can("agenda:read") && <AgendaModule can={can} />}
        {activeTab === "actes" && can("acts:read") && <ActTypesModule can={can} />}
        {activeTab === "phlebotomy" && can("phlebotomy:read") && <PhlebotomyModule can={can} />}
        {activeTab === "planning" && can("planning:read") && <PlanningModule can={can} />}
        {activeTab === "stats" && can("stats:read") && <LabStatsModule can={can} />}
        {activeTab === "sil" && can("integration:read") && <SILIntegrationModule can={can} />}
        {activeTab === "staff" && can("staff:read") && <StaffModule can={can} />}
        {activeTab === "patients" && can("patients:read") && <PatientsModule can={can} />}
        {activeTab === "support" && can("support:read") && <SupportModule can={can} />}
        {activeTab === "audit" && can("audit:read") && <AuditModule can={can} />}
        {activeTab === "vitrine" && can("staff:write") && <VitrineModule can={can} />}
      </main>
    </div>
  );
}

// Correctif L-CTRLK-MORT : composant manquant (voir plus haut) — copie
// adaptee de GlobalSearchModal (HopitalDashboard.js), repointee sur
// /api/lab/patients.
function LabGlobalSearchModal({ onClose, onSelectPatient }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      axios.get(`${API_URL}/api/lab/patients`, { ...getAuthConfig(), params: { search: q.trim(), limit: 8 } })
        .then((r) => setResults(r.data.patients || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 2000, paddingTop: "12vh" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "94%", maxWidth: 520, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden" }}>
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
          placeholder="Rechercher un patient (nom, numero de dossier, telephone...)"
          style={{ width: "100%", padding: "16px 20px", border: "none", borderBottom: "1px solid #F1F5F9", fontSize: 16, boxSizing: "border-box", outline: "none" }}
        />
        <div style={{ maxHeight: 360, overflowY: "auto" }}>
          {loading && <div style={{ padding: "16px 20px", fontSize: 13, color: "#94A3B8" }}>Recherche...</div>}
          {!loading && q.trim().length >= 2 && results.length === 0 && (
            <div style={{ padding: "16px 20px", fontSize: 13, color: "#94A3B8" }}>Aucun patient trouve.</div>
          )}
          {results.map((p) => (
            <div key={p.patient_id} onClick={() => onSelectPatient(p)}
              style={{ padding: "12px 20px", borderTop: "1px solid #F8FAFC", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 500, fontSize: 14, color: "#0F172A" }}>{p.first_name} {p.last_name}</div>
                <div style={{ fontSize: 12, color: "#94A3B8" }}>{p.patient_number}{p.phone ? ` · ${p.phone}` : ""}</div>
              </div>
              <span style={{ fontSize: 12, color: "#185FA5" }}>Ouvrir →</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default LaboDashboard;
